// Track 5.2 — Session-end report (public template).
//
// On End Session, the client:
//   1. POSTs the session summary to /sessions (the api endpoint already
//      persists best CdA, positions[], segment breakdown, AI fields).
//   2. Opens a one-shot WS to aero-ai with mode="report" and feeds it the
//      summary as the user message. Engine streams back structured JSON
//      (headline / biggest_win / position_ranking / next_steps /
//      predicted_tt_gain_w).
//   3. Parses + renders the JSON into a polished card.
//   4. POSTs the parsed JSON to /sessions/:id/report so the dashboard can
//      replay it later without re-running inference.
//
// This component is content-only; the parent (AIClientVision) owns the
// "End Session" trigger and the summary payload.

import { refAeroDistanceLabel, refAeroSpeedLabel, useUnitSystem } from '../services/useUnitSystem';
import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme';

export interface SessionReportData {
  headline: string;
  biggest_win: string;
  position_ranking: Array<{ position: string; cda: number; watts_at_40: number }>;
  next_steps: string[];
  predicted_tt_gain_w: number;
  /** Rapha variant only — Track 5.2 Rapha. */
  recommendations?: Array<{
    sku: string;
    name: string;
    url: string;
    price: string;
    est_watts_saved_min: number;
    est_watts_saved_max: number;
  }>;
}

interface Props {
  /** Session summary payload sent to the engine. */
  summary: Record<string, any>;
  /** "report" | "report_rapha". Rapha variant gets product recs + Korean. */
  mode: 'report' | 'report_rapha';
  locale?: string;
  /** WS URL of the aero-ai service. */
  aeroAiWsUrl: string;
  /** API base for persisting the parsed report. */
  apiBase: string;
  /** Auth token for the POST. */
  authToken: string;
  /** Session id returned from POST /sessions; null until that completes. */
  sessionId: string | null;
}

export default function SessionReport({
  summary,
  mode,
  locale,
  aeroAiWsUrl,
  apiBase,
  authToken,
  sessionId,
}: Props) {
  const units = useUnitSystem();
  const { t } = useTranslation('session');
  const [report, setReport] = useState<SessionReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState<string>('');
  // Bumping `attempt` re-runs the WS effect so a Retry button can
  // ask the model to regenerate the report without unmounting the
  // component. Nothing else in the effect deps depends on the value.
  const [attempt, setAttempt] = useState(0);
  // Track 5.3 — cross-session delta. Fetched in parallel with the report
  // generation so it lands at the same time and the rider sees both at once.
  const [profile, setProfile] = useState<{
    best_cda?: number;
    best_by_position?: Record<string, number>;
  } | null>(null);
  useEffect(() => {
    if (!authToken) return;
    fetch(`${apiBase}/sessions/me/profile`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => setProfile(p))
      .catch(() => {});
  }, [apiBase, authToken]);

  const todayBest = summary.best_cda as number | undefined;
  const allTimeBest = profile?.best_cda;
  const crossSessionDelta =
    todayBest != null && allTimeBest != null && allTimeBest > 0
      ? (allTimeBest - todayBest) * 0.5 * 1.225 * Math.pow(11.11, 3)
      : null;

  useEffect(() => {
    let closed = false;
    let buffer = '';
    // Clear any prior state so a retry starts fresh.
    setError(null);
    setRaw('');
    setReport(null);

    let ws: WebSocket;
    try {
      ws = new WebSocket(aeroAiWsUrl);
    } catch (err) {
      setError(String(err));
      return;
    }

    ws.onopen = () => {
      if (closed) return;
      ws.send(
        JSON.stringify({
          type: 'ask',
          msg: JSON.stringify(summary),
          mode,
          locale,
          context: summary.context,
          sensors: summary.sensors,
        }),
      );
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'token') {
          buffer += msg.content;
          setRaw(buffer);
        } else if (msg.type === 'done') {
          const parsed = tryParseLenient(buffer);
          if (parsed) {
            setReport(parsed as SessionReportData);
            persistReport(parsed as SessionReportData);
          } else {
            setError(
              "Couldn't parse the AI-generated report — the model returned malformed JSON. Retry usually clears this.",
            );
          }
          ws.close();
        } else if (msg.type === 'error') {
          setError(msg.content || 'aero-ai error');
          ws.close();
        }
      } catch (err) {
        console.warn('report ws parse', err);
      }
    };

    ws.onerror = () => setError('Connection error');

    return () => {
      closed = true;
      try { ws.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, locale, aeroAiWsUrl, attempt]);

  const persistReport = async (parsed: SessionReportData) => {
    if (!sessionId) return;
    try {
      await fetch(`${apiBase}/sessions/${sessionId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(parsed),
      });
    } catch (err) {
      console.warn('persist report failed', err);
    }
  };

  if (error) {
    return (
      <Card>
        <Stack spacing={1.5}>
          <Typography sx={{ color: colors.danger, fontSize: '0.875rem' }}>
            {error}
          </Typography>
          <Box>
            <Button
              variant="contained"
              onClick={() => setAttempt((n) => n + 1)}
              sx={{
                backgroundColor: colors.brand,
                color: colors.textInverse,
                fontWeight: 700,
                '&:hover': { backgroundColor: colors.brandBright },
              }}
            >
              Retry
            </Button>
          </Box>
        </Stack>
      </Card>
    );
  }

  if (!report) {
    // Wave 20.47e — was rendering the raw streaming JSON while
    // Claude was still emitting tokens. Rider report: "The report
    // starts by showing json. It should not show json ever."
    // Loading state now shows a friendly message + a subtle
    // shimmer bar; the final rendered report replaces this once
    // JSON parses successfully.
    return (
      <Card>
        <Typography sx={titleSx}>{t('report.generating')}</Typography>
        <Typography sx={bodySx}>{t('report.thinking')}</Typography>
        <Box
          sx={{
            mt: 1.5,
            height: 4,
            borderRadius: 2,
            background: `linear-gradient(90deg, transparent, ${colors.brand}, transparent)`,
            backgroundSize: '200% 100%',
            animation: 'aeroReportShimmer 1.6s ease-in-out infinite',
            '@keyframes aeroReportShimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' },
            },
          }}
        />
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Card>
        <Typography sx={titleSx}>{t('report.title')}</Typography>
        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text,
            mt: 0.5,
            lineHeight: 1.3,
          }}
        >
          {report.headline}
        </Typography>
        <Typography sx={{ ...bodySx, mt: 1.5 }}>{report.biggest_win}</Typography>
      </Card>

      <Card>
        <Typography sx={titleSx}>{t('report.positionRanking')}</Typography>
        <Stack spacing={1} sx={{ mt: 1.25 }}>
          {report.position_ranking.map((row, i) => (
            <Box
              key={row.position}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                p: 1,
                borderRadius: 1.5,
                backgroundColor: i === 0 ? 'rgba(106, 255, 69, 0.06)' : colors.bgSubtle,
                border: `1px solid ${i === 0 ? colors.borderFocus : colors.border}`,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{row.position}</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                <Typography sx={{ fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  {row.cda.toFixed(3)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
                  {row.watts_at_40} W @ 40
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Card>

      <Card>
        <Typography sx={titleSx}>{t('report.nextSteps')}</Typography>
        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
          {report.next_steps.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: colors.brand,
                  mt: '7px',
                }}
              />
              <Typography sx={bodySx}>{s}</Typography>
            </Box>
          ))}
        </Stack>
      </Card>

      <Card>
        <Typography sx={titleSx}>{t('report.predictedTt', { refDist: refAeroDistanceLabel(units) })}</Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: colors.brand, mt: 0.5 }}>
          +{report.predicted_tt_gain_w.toFixed(1)} W
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
          {t('report.predictedTtBody', { refSpeed: refAeroSpeedLabel(units) })}
        </Typography>
      </Card>

      {/* Track 5.3 — cross-session delta. Only renders when the rider has
          at least one prior session (allTimeBest present) AND the math
          produced a meaningful number. */}
      {crossSessionDelta != null && Math.abs(crossSessionDelta) >= 0.5 && (
        <Card>
          <Typography sx={titleSx}>{t('report.crossSession.title')}</Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: crossSessionDelta > 0 ? colors.brand : colors.warning,
              mt: 0.5,
            }}
          >
            {t('report.crossSession.wattsDelta', {
              sign: crossSessionDelta >= 0 ? '+' : '',
              watts: Math.abs(crossSessionDelta).toFixed(1),
              position: report.position_ranking[0]?.position ?? '—',
            })}
          </Typography>
        </Card>
      )}

      {/* Track 5.2 Rapha — product recommendations live in the
          RaphaSessionReport wrapper, but for the public mode we surface
          recommendations inline if the engine returned any (it shouldn't
          for mode=report, but defensive rendering helps QA). */}
      {report.recommendations && report.recommendations.length > 0 && (
        <Card>
          <Typography sx={titleSx}>{t('report.recommendations')}</Typography>
          <Stack spacing={1.25} sx={{ mt: 1.25 }}>
            {report.recommendations.map((r) => (
              <Box
                key={r.sku}
                component="a"
                href={r.url}
                target="_blank"
                rel="noreferrer"
                sx={{
                  display: 'block',
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: colors.bgSubtle,
                  border: `1px solid ${colors.border}`,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { borderColor: colors.borderFocus },
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>{r.name}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
                  {r.price} · saves {r.est_watts_saved_min}–{r.est_watts_saved_max} W
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

/** Defensive: strip Markdown ```json … ``` fences (models still emit
 *  them despite the system prompt) and any surrounding prose, then
 *  slice from the first `{` to the last `}`. If we can't find a
 *  balanced-looking payload, return the trimmed original so the JSON
 *  parser produces a real error rather than us silently swallowing. */
function extractJson(raw: string): string {
  let s = raw.trim();
  // Remove a leading ``` / ```json fence + trailing ``` fence — the
  // fence pattern varies by whether the model added a language tag or
  // trailing whitespace, so match both ends loosely.
  s = s.replace(/^```[a-zA-Z0-9_-]*\s*/i, '');
  s = s.replace(/```\s*$/i, '');
  s = s.trim();
  if (s.startsWith('{') && s.endsWith('}')) return s;
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first >= 0 && last > first) return s.slice(first, last + 1);
  return s;
}

/** Model-quirk cleanup: strip things JSON.parse won't accept but that
 *  the model routinely emits. Applied only as a fallback after a
 *  strict parse fails, so valid JSON is never mangled. */
function repairJson(s: string): string {
  return (
    s
      // Trailing commas before `}` or `]`.
      .replace(/,(\s*[}\]])/g, '$1')
      // JS-style comments (line + block).
      .replace(/\/\/[^\n\r]*/g, '')
      .replace(/\/\*[\s\S]*?\*\//g, '')
      // Smart quotes → straight quotes.
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
  );
}

/** Try strict parse first; if that fails, try again after the
 *  repair pass. Returns null when neither succeeds. */
function tryParseLenient(raw: string): unknown | null {
  const extracted = extractJson(raw);
  try {
    return JSON.parse(extracted);
  } catch {
    try {
      return JSON.parse(repairJson(extracted));
    } catch (err) {
      console.warn('SessionReport JSON parse failed after repair:', err, extracted);
      return null;
    }
  }
}

const titleSx = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: colors.textMuted,
};
const bodySx = {
  fontSize: '0.875rem',
  color: colors.text,
  lineHeight: 1.6,
};

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      {children}
    </Box>
  );
}
